"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Calendar, CalendarClock, Mail } from "lucide-react";
import accessURL from "../../../../components/utils/superAccess";

interface PricingOption {
  duration: string;
  amount: string;
}

const PricingSection: React.FC = () => {
  const [selectedDuration, setSelectedDuration] = useState<string>("");
  const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const features = [
    "Create and manage sales representatives",
    "Create and manage managers",
    "Customer database management",
    "Schedule and track visits",
    "Generate comprehensive reports",
    "Real-time activity tracking",
  ];

  // Get the appropriate display name for each duration
  const getDurationDisplayName = (duration: string): string => {
    if (duration === "3months") return "3 Months";
    if (duration === "6months") return "6 Months";
    if (duration === "1year") return "1 Year";
    return duration;
  };

  // Get duration in months text
  const getDurationText = (duration: string): string => {
    if (duration === "3months") return "3 months";
    if (duration === "6months") return "6 months";
    if (duration === "1year") return "12 months";
    return duration;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pricingRes = await Promise.all([accessURL.get("getPricing")]);

        // Extract all pricing options and filter out those with amount of 0
        const allPricing = pricingRes[0].data.data.filter(
          (option: PricingOption) =>
            option.amount !== "0" && parseFloat(option.amount) !== 0
        );
        setPricingOptions(allPricing);

        // Set default selected duration to the first option if exists
        if (allPricing && allPricing.length > 0) {
          setSelectedDuration(allPricing[0].duration);
        }
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get the current selected price
  const getCurrentPrice = (): string => {
    const selected = pricingOptions.find(
      (option) => option.duration === selectedDuration
    );
    return selected ? `USD${parseFloat(selected.amount).toLocaleString()}` : "";
  };

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-16 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl font-extrabold mb-4 text-gray-900">
          Simple, Transparent Pricing
        </h2>
        <p className="text-lg text-gray-600 mb-12">
          Choose the duration that works best for your business
        </p>

        {/* Duration Toggle */}
        <div className="flex flex-col items-center justify-center mb-12">
          <div className="flex items-center gap-4 p-2 bg-gray-100 rounded-full mb-4">
            {pricingOptions.map((option) => (
              <button
                key={option.duration}
                onClick={() => setSelectedDuration(option.duration)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 ${
                  selectedDuration === option.duration
                    ? "bg-white text-gray-900 shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {option.duration === "1year" ? (
                  <CalendarClock className="w-5 h-5" />
                ) : (
                  <Calendar className="w-5 h-5" />
                )}
                <span className="font-medium">
                  {getDurationDisplayName(option.duration)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12">
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Business CRM Suite
            </h3>
            <p className="text-gray-600">
              Complete CRM solution for managing your business operations
            </p>
          </div>

          <div className="mb-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedDuration}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col items-center"
              >
                {loading ? (
                  <div className="animate-pulse h-12 w-32 bg-gray-200 rounded"></div>
                ) : (
                  <div className="flex items-baseline">
                    <span className="text-5xl font-extrabold text-gray-900">
                      {getCurrentPrice()}
                    </span>
                  </div>
                )}
                <span className="text-gray-600 mt-2">
                  One-time payment for {getDurationText(selectedDuration)}
                </span>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="grid gap-4 mb-8 max-w-md mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 text-left"
              >
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-600">{feature}</span>
              </motion.div>
            ))}
          </div>

          <div className="flex items-center justify-center space-x-2">
            <button className="md:w-auto md:min-w-[220px] inline-flex items-center justify-center px-6 py-3 rounded-lg bg-black text-white font-medium hover:bg-gray-900 transition duration-300">
              <a
                href="mailto:support@syncai.ltd"
                className="inline-flex items-center text-white"
              >
                <Mail size={18} className="mr-1" />
                Contact Us
              </a>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
